import React, { useState, useEffect } from "react";
import {
  ConfigProvider,
  Button,
  Space,
  Typography,
  Card,
  Skeleton,
  Divider,
  Grid,
  Flex,
  Checkbox,
  Affix,
} from "antd";
import {
  LockOutlined,
  UserOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { ProForm, ProFormText } from "@ant-design/pro-components";

import logo from "../assets/logos/company.png";
import loader from "src/assets/img/loading.gif";

import executeRecaptcha from "../common/utils/grecaptcha";
import validation from "../common/utils/validation";
import apiRequest from "../common/utils/apiRequest";
import firebase from "src/firebase/firebase";
import { motion, AnimatePresence } from "motion/react";

import "./css/SignIn.css";
import coreFunctions from "src/common/utils/coreFunctions";

const { Title, Link, Text } = Typography;

const LoginPage: React.FC = () => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [disableButton, setDisableButton] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailError, setEmailError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [loginBtnText, setLoginBtnText] = useState("Sign In");
  const [reInitializeSession, setReInitializeSession] = useState(true);

  const screens = Grid.useBreakpoint();
  const navigate = useNavigate();

  // Theme toggle
  const toggleTheme = () => setIsDarkMode((prev) => !prev);

  // Sign In Handler
  const handleSignIn = () => {
    const isEmailValid = validation.validateEmail(email);
    const isPasswordValid = validation.validatePassword(password);

    setEmailError(!isEmailValid);
    setPasswordError(!isPasswordValid);

    if (!isEmailValid || !isPasswordValid) return;

    setDisableButton(true);
    localStorage.setItem("waiting", "1");

    const API = new apiRequest("authorize", false, true);
    API.send({ username: email, password }, (data: any) => {
      if (data.status === "success") {
        firebase();

        // Store tokens and user details
        localStorage.setItem("username", email);
        localStorage.setItem("token", data.token);
        localStorage.setItem("user_lng", data.language);
        localStorage.setItem("role", data.role);
        localStorage.setItem("last_logined", data.last_logined);
        localStorage.setItem("profile_image", data.profile_image);

        localStorage.setItem(
          "toast",
          JSON.stringify({
            type: "success",
            message: "Welcome Back " + data.user,
            description: "Last Login: " + data.last_logined,
            duration: 3,
            placement: "bottomRight",
          })
        );

        localStorage.removeItem("waiting");
        navigate("/dashboard", { replace: true });
      } else {
        setLoginError(data.message);
        setLoginBtnText(data.message);
        setTimeout(() => {
          setLoginError("");
          setLoginBtnText("Sign In");
          handleGCaptcha();
        }, 2000);
      }
    });
  };

  // gRecaptcha handler
  const handleGCaptcha = async () => {
    try {
      setDisableButton(true);
      await executeRecaptcha("submit_form");
      setLoading(false);
    } catch (err) {
      console.error("gRecaptcha error:", err);
    } finally {
      setDisableButton(false);
    }
  };

  // Token check on page load
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      const API = new apiRequest("authorize_token", true, true);
      API.send({ token }, (data: any) => {
        if (data.status === "success") {
          navigate(data.redirect, { replace: true });
        } else {
          setReInitializeSession(false);
        }
      });
    } else {
      setReInitializeSession(false);
    }

    handleGCaptcha();
  }, []);

  return (
    <ConfigProvider
      theme={{
        token: { colorPrimary: "#007bff", borderRadius: 8 },
      }}
    >
      <ProForm submitter={false}>
        <Flex
          vertical
          justify="space-between"
          style={{
            backgroundColor: isDarkMode ? "#141414" : "#fff",
            overflow: "hidden",
          }}
        >
          {/* Top Container */}
          <Flex vertical align="center" style={{ flex: 1, padding: 16 }}>
            {reInitializeSession ? (
              <Flex
                gap="middle"
                align="center"
                style={{ width: "100%", marginTop: "10%" }}
                vertical
              >
                <img src={loader} alt="Loading..." width={20} />
              </Flex>
            ) : (
              <AnimatePresence mode="wait" initial={true}>
                <motion.div
                  key="login-card"
                  style={{ marginTop: "10%" }}
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0 }}
                >
                  <Card
                    style={{
                      width: "100%",
                      maxWidth: 380,
                      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                      backgroundColor: isDarkMode ? "#1f1f1f" : "#fff",
                      paddingBottom: 20,
                    }}
                  >
                    <Flex justify="center" align="center">
                      <img src={logo} alt="Logo" style={{ width: "60%" }} />
                    </Flex>

                    <Divider plain>
                      <Title level={3} style={{ margin: 0 }}>
                        Login
                      </Title>
                    </Divider>

                    {loading ? (
                      <>
                        <Skeleton.Input
                          active
                          block
                          style={{ marginBottom: 10, marginTop: 25 }}
                        />
                        <Skeleton.Input
                          active
                          block
                          style={{ marginBottom: 30 }}
                        />
                        <Skeleton.Input
                          active
                          block
                          style={{ marginBottom: 20 }}
                        />
                      </>
                    ) : (
                      <Space
                        direction="vertical"
                        size="small"
                        style={{ width: "100%" }}
                      >
                        <ProFormText
                          name="email"
                          fieldProps={{
                            prefix: <UserOutlined />,
                            value: email,
                            onChange: (e) => setEmail(e.target.value),
                            status: emailError ? "error" : "",
                            suffix: emailError && (
                              <ExclamationCircleOutlined
                                style={{ color: "red" }}
                              />
                            ),
                            placeholder: "name@example.com",
                            size: "large",
                          }}
                          rules={[
                            { required: true, message: "Email is required" },
                            {
                              type: "email",
                              message: "Please enter a valid email",
                            },
                          ]}
                        />

                        <ProFormText.Password
                          name="password"
                          fieldProps={{
                            prefix: <LockOutlined />,
                            value: password,
                            onChange: (e) => setPassword(e.target.value),
                            status: passwordError ? "error" : "",
                            suffix: passwordError && (
                              <ExclamationCircleOutlined
                                style={{ color: "red" }}
                              />
                            ),
                            placeholder: "Password",
                            size: "large",
                          }}
                          rules={[
                            {
                              required: true,
                              message: "Please enter your password",
                            },
                          ]}
                        />

                        <Flex justify="space-between" align="center">
                          <Checkbox checked disabled>
                            Keep me signed in
                          </Checkbox>
                          <Link href="/forgot-password">Forgot Password?</Link>
                        </Flex>

                        <Button
                          style={{ marginTop: "10px" }}
                          type="primary"
                          block
                          size="large"
                          htmlType="submit"
                          loading={disableButton}
                          onClick={handleSignIn}
                          danger={loginError !== ""}
                        >
                          {loginBtnText}
                        </Button>
                      </Space>
                    )}
                  </Card>
                </motion.div>
              </AnimatePresence>
            )}
          </Flex>

          <div
            style={{
              position: "fixed",
              bottom: 0,
              left: 0,
              width: "100%",
              borderTop: "1px solid #d9d9d9",
              padding: "10px",
              zIndex: 1000,
            }}
          >
            <Flex justify="space-between" align="center">
              <Text type="secondary">
                This site is protected by{" "}
                <Link href="/privacy">Privacy Policy</Link>
              </Text>
              <Space style={{ paddingRight: "15px" }}>
                {!coreFunctions.isMobileDevice() && (
                  <Space>
                    Secured with <i>Fortified Intelligence</i> |
                  </Space>
                )}
                Powered by
                <Link href="https://globalcloudmedia.lk/">GCM</Link>
              </Space>
            </Flex>
          </div>
        </Flex>
      </ProForm>
    </ConfigProvider>
  );
};

export default LoginPage;
